/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Filter } from 'react-feather';
import { formatDate } from '../../../utils/date-utils';
import useTranslation from '../../../utils/translation-utils';
import Badge, { BadgeSizes, BadgeTypes } from '../badge';
import Button, { ButtonTypes } from '../button';
import DateRangePicker from '../date-range-picker';
import { DropdownItem, DropdownTypes } from '../dropdown';
import Icon, { IconSizes, IconColors } from '../icon';
import Modal from '../modal';
import { FilterTypes } from '../table/table-toolbar/constants';
import RenBadges from '../table/renderers/ren-badges';
import './mobile-filter.scss';
import { getRenderIcon } from '../../../utils/general-utils';

const CARD_FILTER_ACCESSOR = 'card-filter';

const MobileFilter = ({ filters }) => {
  const [getTranslation] = useTranslation();
  const [open, setOpen] = useState(false);
  const [filterBy, setFilterBy] = useState(null);
  const [valuesMap, setValuesMap] = useState({});
  const [range, setRange] = useState({
    startDate: null,
    endDate: null,
  });

  const originalValuesMap = useRef();

  useEffect(() => {
    const { valuesMap: valuesMapObj } = filters;
    originalValuesMap.current = { ...valuesMapObj };
  }, []);

  useEffect(() => {
    if (!filterBy) {
      setRange({
        startDate: null,
        endDate: null,
      });
    }
  }, [filterBy]);

  useEffect(() => {
    const { valuesMap: valuesMapObj } = filters;
    if (open) {
      setValuesMap(valuesMapObj);
    }
  }, [filters.valuesMap, open]);

  const handleFilterChange = (
    filterId,
    itemId,
    isSingle = false,
    allowDeselection = true,
  ) => {
    const modifiedValuesMap = { ...valuesMap };

    const selectedIds = valuesMap[filterId] || [];
    if (selectedIds.includes(itemId)) {
      if (allowDeselection) {
        modifiedValuesMap[filterId] = selectedIds.filter((val) => val !== itemId);
      } else {
        return;
      }
    } else {
      modifiedValuesMap[filterId] = isSingle ? [itemId] : [...selectedIds, itemId];
    }
    setValuesMap(modifiedValuesMap);
  };

  const handleClose = () => {
    if (!filterBy) {
      setValuesMap({});
      setOpen(false);
    } else {
      setFilterBy(null);
    }
  };

  const handleApplyFilter = () => {
    const { onChange } = filters;
    if (onChange && typeof onChange === 'function') {
      onChange(valuesMap);
    }
    handleClose();
  };

  const handleClearAll = () => {
    setValuesMap(originalValuesMap.current);
  };

  const getMultiFilter = () => {
    const fillSelectedPropInItems = (items, selectedIds = []) => items?.map((item) => ({
      ...item, selected: selectedIds.includes(item.id),
    }));

    const { items, id } = filterBy;
    const itemsList = fillSelectedPropInItems(items, valuesMap[id]);
    return itemsList.map(({ selected, label, id: idx }) => (
      <DropdownItem
        key={idx}
        id={idx}
        type={DropdownTypes.MULTI_SELECT}
        selected={selected}
        label={label}
        onClick={() => handleFilterChange(id, idx)}
      />
    ));
  };

  const getSingleFilter = () => {
    const {
      items, id, allowDeselection, withClearSelection,
    } = filterBy;
    const fillSelectedPropInItems = () => items?.map((item) => ({
      ...item, selected: valuesMap[id]?.includes(item.id),
    }));
    const itemsList = fillSelectedPropInItems();

    return itemsList.map(({
      selected, label, id: idx,
    }) => (
      <DropdownItem
        key={idx}
        id={idx}
        type={DropdownTypes.SINGLE_SELECT}
        selected={selected}
        label={label}
        withClearSelection={withClearSelection}
        onClick={() => {
          handleFilterChange(id, idx, true, allowDeselection);
          setFilterBy(null);
        }}
      />
    ));
  };

  const getDateRangeFilter = () => {
    const handleDateRangeSelect = (selectedRange) => {
      setRange({
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      });
    };
    const handleDateRangeApply = (filterId, startDate, endDate) => {
      const modifiedValuesMap = { ...valuesMap };
      modifiedValuesMap[filterId] = {
        startDate,
        endDate,
      };
      setValuesMap(modifiedValuesMap);
      setFilterBy(null);
    };
    const { id } = filterBy;
    return (
      <DateRangePicker
        startDate={valuesMap[id] ? valuesMap[id].startDate : ''}
        endDate={valuesMap[id] ? valuesMap[id].endDate : ''}
        minDate={valuesMap[id] ? valuesMap[id].minDate : ''}
        maxDate={valuesMap[id] ? valuesMap[id].maxDate : ''}
        onChange={(stDt, enDt) => handleDateRangeApply(id, stDt, enDt)}
        onRangeSelect={handleDateRangeSelect}
        noInput
        months={1}
        className="cards-filter"
        backdrop={false}
        showInputView={false}
        defaultRanges={[]}
        onCancel={() => setFilterBy(null)}
      />
    );
  };

  const getFilterByView = () => {
    const { type } = filterBy;
    switch (type) {
      case FilterTypes.MULTI_FILTER:
        return getMultiFilter();
      case FilterTypes.DATE_RANGE:
        return getDateRangeFilter();
      default:
        return getSingleFilter();
    }
  };

  const getFilterList = () => {
    const getValuesFromIds = (id) => {
      const { list } = filters;
      const { items } = list.find((filterItem) => filterItem.id === id);
      const selectedValuesList = items.filter((item) => valuesMap[id].includes(item.id))
        .map((el) => ({ ...el, size: BadgeSizes.SMALL, showDot: false }));
      const data = {
        [CARD_FILTER_ACCESSOR]: selectedValuesList,
      };
      return data;
    };
    const getBadgeView = ({ type, id }) => {
      if (!valuesMap[id]) {
        return null;
      }
      switch (type) {
        case FilterTypes.MULTI_FILTER: {
          const data = getValuesFromIds(id);
          return <RenBadges data={data} accessor={CARD_FILTER_ACCESSOR} />;
        }
        case FilterTypes.DATE_RANGE:
          return (
            <Badge
              label={`${formatDate(valuesMap[id].startDate)} - ${formatDate(valuesMap[id].endDate)}`}
              showDot={false}
              type={BadgeTypes.PRIMARY}
              size={BadgeSizes.SMALL}
            />
          );
        default: {
          if (!valuesMap[id][0]) return null;
          const data = getValuesFromIds(id);
          return (
            <Badge {...data[CARD_FILTER_ACCESSOR][0]} />
          );
        }
      }
    };
    const { list } = filters;
    return (
      <div className="card-filter-list">
        {list.map((filterItem) => (
          <div className="card-filter-item" onClick={() => setFilterBy(filterItem)}>
            <div>{filterItem.label}</div>
            <div className="card-badges">
              {getBadgeView(filterItem)}
            </div>
            <div>
              <Icon
                icon={ChevronRight}
                size={IconSizes.MEDIUM}
                color={IconColors.DARK}
              />
            </div>
          </div>
        ))}
        <div className="card-filter-actions">
          <Button
            label={getTranslation('DTP_APPLY')}
            onClick={handleApplyFilter}
          />
          <Button
            label={getTranslation('CLEAR_ALL')}
            type={ButtonTypes.SECONDARY}
            onClick={handleClearAll}
          />
        </div>
      </div>
    );
  };

  const getFilterButton = () => {
    const values = filters.valuesMap || {};
    const appliedFiltersCount = Object.keys(values)?.reduce((acc, key) => {
      if (key === 'date') {
        return acc + 1;
      }
      return acc + values[key].length;
    }, 0);

    if (appliedFiltersCount > 0) {
      return (
        <div className="applied-filter-btn-cont" onClick={() => setOpen(!open)}>
          <div className="af-bc-count">{appliedFiltersCount}</div>
          <Icon
            icon={Filter}
            size={IconSizes.MEDIUM}
            color={IconColors.PRIMARY}
            className="af-bc-filter-btn"
            onClick={() => setOpen(!open)}
          />
        </div>
      );
    }

    return (
      <Button
        borderLess
        renderIcon={getRenderIcon(Filter)}
        type={ButtonTypes.SECONDARY}
        className="cfc-button"
        onClick={() => setOpen(!open)}
      />
    );
  };

  const startDate = range.startDate ? range.startDate : valuesMap?.date?.startDate;
  const endDate = range.endDate ? range.endDate : valuesMap?.date?.endDate;
  return (
    <div className="cards-filter-cont">
      {getFilterButton()}
      <Modal
        open={open}
        header={
          (filterBy && filterBy.label && filterBy.label.toUpperCase() === 'DATE')
            ? (
              <div className="modal-header-val">
                {`${getTranslation('FILTER_BY')} : `}
                <span className="date-part">
                  {`${formatDate(startDate)} - ${formatDate(endDate)}`}
                </span>
              </div>
            )
            : `${getTranslation('FILTER_BY')} : ${(filterBy?.label || '')}`
        }
        showClose={!filterBy}
        onClose={handleClose}
        showBackArrow={filterBy}
        onBackArrowClick={() => setFilterBy(null)}
        className="card-filter-modal"
      >
        <div className="card-filter-modal-body">
          {!filterBy
            ? getFilterList()
            : getFilterByView()}
        </div>
      </Modal>
    </div>
  );
};

MobileFilter.defaultProps = {
  filters: null,
};

MobileFilter.propTypes = {
  filters: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape),
    valuesMap: PropTypes.shape(),
    onChange: PropTypes.func,
  }),
};

export default MobileFilter;
